/**
 * The **as-designed** tree: a revision exploded through its children's
 * revisions, down to the parts nothing is made of.
 *
 * §2.6 gives us one structure with two readers. `tree.ts` is the as-built
 * reader — it walks open installations and answers "what is in R-07 right
 * now". This is the as-designed reader, and it walks bills of materials
 * instead: it answers "what is a robot *supposed* to be", which is a question
 * that has an answer before a single part has been received. That is the whole
 * reason it exists as a separate function rather than a mode of `buildTree`:
 * the design of the robot is knowable on the day the item master is typed in,
 * and the fleet is not.
 *
 * The recursion stops at the parts that are made of nothing — bought
 * fasteners and cameras, printed mounts, machined plates, bearings. Which
 * stop it and *why* is the useful half of the output (`DesignLeaf`), because
 * "nobody has given this assembly a revision yet" and "this is a bought part"
 * look identical in a tree that only records that it ended.
 *
 * `requirementsOf` in `bom.ts` is deliberately one level deep, for the build
 * order that allocates against a single revision. This is the multi-level
 * walk, and it is still not §4.6's `explode()`: that one nets off on-hand and
 * on-order stock and offsets lead times, which needs the ledger. This knows
 * nothing but structure.
 *
 * ## Why the inputs are structural
 *
 * The parameters below ask for the four fields of a type and the four fields
 * of a line that an explosion actually reads, rather than for `ComponentType`
 * and `Revision` from `types.ts`. Those two satisfy them — but so does the
 * SPA's wire object and so does a row out of the API, with no cast at the
 * boundary and no fixture in a test having to invent a `defaultVendorId` it
 * does not care about. A function both halves can call without ceremony is
 * the point of this package.
 */

import { childPath } from './positions'
import type { Path, PositionName } from './positions'
import type { ComponentKind, Tracking } from './types'

/** What an explosion needs to know about a type — a subset of `ComponentType`. */
export interface DesignTypeRef {
  readonly id: string
  readonly name: string
  readonly kind: ComponentKind
  readonly tracking: Tracking
}

/** A subset of `BomLine`. `substitutes` are not walked: a substitute is what
 *  may be fitted instead, not what the thing is designed to be. */
export interface DesignLineRef {
  readonly childTypeId: string
  /** Null or absent means any revision of that type will do — see `currentRevisionOf`. */
  readonly childRevisionId?: string | null
  readonly quantity: number
  /** Present exactly when the child is serialized. Null means consumed. */
  readonly position: string | null
}

/** A subset of `Revision`. */
export interface DesignRevisionRef {
  readonly id: string
  readonly componentTypeId: string
  readonly name: string
  /** ISO calendar day. Only compared, never parsed — ISO days sort as strings. */
  readonly effectiveFrom: string
  readonly supersededById?: string | null
  readonly lines: readonly DesignLineRef[]
}

export interface DesignSource {
  readonly types: readonly DesignTypeRef[]
  readonly revisions: readonly DesignRevisionRef[]
}

/**
 * Why the explosion stopped here. Null on an interior node.
 *
 * Six values rather than one boolean, because a leaf is not one thing:
 * `purchased` and `empty` are the tree working as designed, `needs_revision`
 * and `unknown_type` are gaps in the item master that will block a build, and
 * `cycle` and `too_deep` are corrupt data the view has to survive rather than
 * hang on.
 */
export type DesignLeaf =
  /** `buy`: somebody else designed and made it, so it has no revision of ours (§4.2). */
  | 'purchased'
  /** A `make` type nobody has given a revision yet. A gap, and worth saying so. */
  | 'needs_revision'
  /** A line pointing at a type the item master no longer has. A gap, not a leaf. */
  | 'unknown_type'
  /** A revision with no lines — a machined plate or a printed mount, made of nothing. */
  | 'empty'
  /** A quantity line: on the bill of materials, never in the tree (§14). */
  | 'consumed'
  /** This type already appears above it. Cannot happen through the API — `checkBomLine`
   *  refuses self-reference — but an indirect loop entered another way must not hang. */
  | 'cycle'
  | 'too_deep'

export interface DesignNode {
  /**
   * Unique within the tree and stable across loads: the route that reached it.
   *
   * A path alone will not do, because two quantity lines of the same type are
   * legal and neither has a position (`bom.test.ts` asserts that), so both
   * would land on the parent's path.
   */
  readonly key: string
  readonly typeId: string
  /** Falls back to the id when the item master has never heard of the type. */
  readonly typeName: string
  readonly kind: ComponentKind
  readonly tracking: Tracking
  /** The revision this node is exploded through; null at a leaf with none. */
  readonly revisionId: string | null
  readonly revisionName: string | null
  /** Its position in its parent. Null at the root and on a consumed line. */
  readonly position: PositionName | null
  /** Dotted route of positions from the root. Empty at the root; the parent's
   *  path on a consumed line, which has no position of its own. */
  readonly path: Path
  /** How many of it one of its *parent* needs. Always 1 on a position. */
  readonly quantity: number
  /** How many of it one whole root needs — this node's quantity multiplied by
   *  every quantity above it. Fourteen screws in each of two arms is 28. */
  readonly perRoot: number
  readonly depth: number
  readonly leaf: DesignLeaf | null
  readonly children: readonly DesignNode[]
}

/**
 * The revision a type is currently built to.
 *
 * Superseded revisions are skipped: a revision that has been replaced is still
 * the truth about a robot built in September and the wrong answer for what to
 * build tomorrow. `effectiveFrom` decides between the rest, latest first, and
 * the name breaks a tie — arbitrary, but *stable*, so a diagram does not
 * reshape itself between two loads of the same data.
 *
 * If every revision has been superseded — a chain whose end points outside the
 * set — the whole list is reconsidered rather than nothing returned. Somebody
 * asking what a thing is made of is better served by a stale answer than by a
 * blank screen, and the node says which revision it used.
 */
export function currentRevisionOf(
  revisions: readonly DesignRevisionRef[],
  typeId: string,
): DesignRevisionRef | undefined {
  const mine = revisions.filter((r) => r.componentTypeId === typeId)
  const live = mine.filter((r) => (r.supersededById ?? null) === null)
  return newest(live.length > 0 ? live : mine)
}

function newest(revisions: readonly DesignRevisionRef[]): DesignRevisionRef | undefined {
  return revisions.reduce<DesignRevisionRef | undefined>((best, r) => {
    if (best === undefined) return r
    if (r.effectiveFrom !== best.effectiveFrom) {
      return r.effectiveFrom > best.effectiveFrom ? r : best
    }
    return r.name > best.name ? r : best
  }, undefined)
}

/**
 * Explode a revision into the as-designed tree.
 *
 * Null when the revision is not in the source — a caller with a stale id gets
 * an empty screen it can explain, not a throw.
 *
 * Children come out positions first, then consumed lines, each group in the
 * order the engineer gave them (§11.2: "the BOM as a tree, with quantity lines
 * under each assembly"). The order within a group is the input's, because
 * `ordinal` is a decision somebody made; the partition between them is ours.
 */
export function designTree(
  rootRevisionId: string,
  source: DesignSource,
  options?: { readonly maxDepth?: number },
): DesignNode | null {
  const maxDepth = options?.maxDepth ?? 24
  const typeById = new Map(source.types.map((t) => [t.id, t]))
  const revisionById = new Map(source.revisions.map((r) => [r.id, r]))

  const root = revisionById.get(rootRevisionId)
  if (root === undefined) return null

  const expand = (
    revision: DesignRevisionRef,
    node: Omit<DesignNode, 'children'>,
    ancestors: ReadonlySet<string>,
  ): DesignNode => {
    const seen = new Set(ancestors).add(node.typeId)
    const lines = [
      ...revision.lines.filter((l) => l.position !== null),
      ...revision.lines.filter((l) => l.position === null),
    ]
    return {
      ...node,
      children: lines.map((line, i) => child(line, i, node, seen)),
    }
  }

  const child = (
    line: DesignLineRef,
    index: number,
    parent: Omit<DesignNode, 'children'>,
    ancestors: ReadonlySet<string>,
  ): DesignNode => {
    const type = typeById.get(line.childTypeId)
    const position = line.position === null ? null : (line.position as PositionName)
    const depth = parent.depth + 1
    const here: Omit<DesignNode, 'children' | 'leaf' | 'revisionId' | 'revisionName'> = {
      // A position is unique within a revision, so it identifies the branch;
      // a consumed line falls back to its index, which is why the index is
      // taken from the *partitioned* list and is therefore stable too.
      key: `${parent.key}>${position ?? `q${index}`}`,
      typeId: line.childTypeId,
      typeName: type?.name ?? line.childTypeId,
      kind: type?.kind ?? 'buy',
      tracking: type?.tracking ?? (position === null ? 'quantity' : 'serialized'),
      position,
      path: position === null ? parent.path : childPath(parent.path, position),
      quantity: line.quantity,
      perRoot: parent.perRoot * line.quantity,
      depth,
    }
    const stop = (leaf: DesignLeaf, revision?: DesignRevisionRef): DesignNode => ({
      ...here,
      revisionId: revision?.id ?? null,
      revisionName: revision?.name ?? null,
      leaf,
      children: [],
    })

    if (type === undefined) return stop('unknown_type')
    // A quantity item is consumed by the build and never enters the tree, so
    // it is a leaf whatever it is made of — a slot no serial can fill is worse
    // than a branch not drawn. Its own structure is still reachable by
    // re-rooting the view on it, which is what the Catalog's tree does.
    if (position === null) return stop('consumed')
    if (type.kind === 'buy') return stop('purchased')

    const named = line.childRevisionId ?? null
    const revision =
      named === null
        ? currentRevisionOf(source.revisions, line.childTypeId)
        : revisionById.get(named)
    if (revision === undefined) return stop('needs_revision')
    if (ancestors.has(line.childTypeId)) return stop('cycle', revision)
    if (revision.lines.length === 0) return stop('empty', revision)
    if (depth >= maxDepth) return stop('too_deep', revision)

    return expand(
      revision,
      { ...here, revisionId: revision.id, revisionName: revision.name, leaf: null },
      ancestors,
    )
  }

  const rootType = typeById.get(root.componentTypeId)
  return expand(
    root,
    {
      key: `r:${root.id}`,
      typeId: root.componentTypeId,
      typeName: rootType?.name ?? root.componentTypeId,
      kind: rootType?.kind ?? 'make_internal',
      tracking: rootType?.tracking ?? 'serialized',
      revisionId: root.id,
      revisionName: root.name,
      position: null,
      path: '' as Path,
      quantity: 1,
      perRoot: 1,
      depth: 0,
      leaf: root.lines.length === 0 ? 'empty' : null,
    },
    new Set(),
  )
}

/** Depth-first, parents before children — the order a list renders in. */
export function flattenDesign(node: DesignNode): readonly DesignNode[] {
  return [node, ...node.children.flatMap(flattenDesign)]
}

/**
 * What the tree adds up to, for the line above a diagram.
 *
 * `gaps` is the half worth acting on: every node the explosion could not get
 * past for a reason somebody can fix. A robot whose base has no revision is a
 * robot that cannot be built, and the tree is the first screen where that is
 * visible at a glance.
 */
export interface DesignSummary {
  readonly nodes: number
  /** Serialized nodes below the root: the slots a part gets scanned into. */
  readonly positions: number
  /** Quantity lines: consumed by the build, never in the tree. */
  readonly consumed: number
  readonly depth: number
  readonly gaps: readonly DesignNode[]
}

const GAP: ReadonlySet<DesignLeaf> = new Set<DesignLeaf>([
  'needs_revision',
  'unknown_type',
  'cycle',
  'too_deep',
])

export function summarizeDesign(root: DesignNode): DesignSummary {
  const all = flattenDesign(root)
  return {
    nodes: all.length,
    positions: all.filter((n) => n.position !== null).length,
    consumed: all.filter((n) => n.leaf === 'consumed').length,
    depth: all.reduce((d, n) => Math.max(d, n.depth), 0),
    gaps: all.filter((n) => n.leaf !== null && GAP.has(n.leaf)),
  }
}

/**
 * Every type one root needs, summed over the whole tree.
 *
 * The recursive counterpart to `requirementsOf`, and the number a person
 * actually asks for: not "an arm needs 14 screws" but "a robot needs 28".
 * Still pure structure — nothing is netted off stock here, which is what
 * separates this from §4.6's `explode()` and is why this one is safe to run in
 * a browser.
 */
export function designRequirements(root: DesignNode): ReadonlyMap<string, number> {
  const out = new Map<string, number>()
  for (const node of flattenDesign(root)) {
    if (node.depth === 0) continue
    out.set(node.typeId, (out.get(node.typeId) ?? 0) + node.perRoot)
  }
  return out
}
